import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Mail, Phone, MapPin, Send, CheckCircle2, Loader2 } from "lucide-react";
import { companyInfo } from "../mock";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Checkbox } from "./ui/checkbox";
import { useToast } from "../hooks/use-toast";
import { CallbackPicker } from "./CallbackPicker";
import { WHATSAPP_URL, WhatsAppIcon } from "./WhatsApp";

const Contact = () => {
  const { toast } = useToast();
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
    consent: false,
    website: "",
    callback_requested: false,
    callback_date: "",
    callback_time: "",
    property_city: "",
    units: "",
    current_manager: "",
    desired_start: "",
  });
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((s) => ({ ...s, [name]: value }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast({ title: "Bitte alle Pflichtfelder ausfüllen.", variant: "destructive" });
      return;
    }
    if (!form.property_city.trim() || !form.units) {
      toast({ title: "Bitte Ort der Immobilie und Anzahl der Wohneinheiten angeben.", variant: "destructive" });
      return;
    }
    if (form.message.trim().length < 10) {
      toast({ title: "Bitte beschreiben Sie Ihr Anliegen etwas ausführlicher.", variant: "destructive" });
      return;
    }
    if (form.callback_requested && !form.phone.trim()) {
      toast({ title: "Für einen Rückruf benötigen wir Ihre Telefonnummer.", variant: "destructive" });
      return;
    }
    if (!form.consent) {
      toast({ title: "Bitte stimmen Sie der Datenverarbeitung zu.", variant: "destructive" });
      return;
    }
    setSending(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, units: form.units === "" ? null : Number(form.units) }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(typeof body.detail === "string" ? body.detail : "request failed");
      }
      setSent(true);
      toast({
        title: "Nachricht gesendet.",
        description: "Wir melden uns schnellstmöglich bei Ihnen.",
      });
      setForm({
        name: "", email: "", phone: "", subject: "", message: "", consent: false, website: "",
        callback_requested: false, callback_date: "", callback_time: "",
        property_city: "", units: "", current_manager: "", desired_start: "",
      });
    } catch (err) {
      toast({
        title: "Senden fehlgeschlagen.",
        description:
          err.message !== "request failed"
            ? err.message
            : `Bitte versuchen Sie es erneut oder schreiben Sie direkt an ${companyInfo.email}.`,
        variant: "destructive",
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div className="text-[12px] tracking-[0.3em] uppercase text-accent2 font-semibold">
              Kontakt
            </div>
            <h2 className="mt-3 font-display text-[36px] md:text-[46px] leading-tight font-extrabold text-brand-900">
              Sprechen Sie mit uns. Kostenlos und unverbindlich.
            </h2>
            <p className="mt-5 text-brand-700 leading-relaxed">
              Ob konkrete Anfrage oder erste Fragen: Wir nehmen uns Zeit für Sie. Kontaktieren
              Sie uns telefonisch, per E-Mail oder über das Formular. Wir melden uns zeitnah.
            </p>

            <div className="mt-10 space-y-5">
              <a
                href={`tel:${companyInfo.phone.replace(/\s/g, "")}`}
                className="flex items-start gap-4 group"
              >
                <div className="w-12 h-12 rounded-2xl bg-brand-900 group-hover:bg-accent2 transition-colors flex items-center justify-center text-white">
                  <Phone className="w-5 h-5" />
                </div>
                <div>
                  <div className="text-[12px] uppercase tracking-wider text-brand-500">Telefon</div>
                  <div className="font-semibold text-brand-900">{companyInfo.phone}</div>
                </div>
              </a>
              <a
                href={`mailto:${companyInfo.email}`}
                className="flex items-start gap-4 group"
              >
                <div className="w-12 h-12 rounded-2xl bg-brand-900 group-hover:bg-accent2 transition-colors flex items-center justify-center text-white">
                  <Mail className="w-5 h-5" />
                </div>
                <div>
                  <div className="text-[12px] uppercase tracking-wider text-brand-500">E-Mail</div>
                  <div className="font-semibold text-brand-900">{companyInfo.email}</div>
                </div>
              </a>
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noreferrer"
                data-testid="contact-whatsapp-link"
                className="flex items-start gap-4 group"
              >
                <div className="w-12 h-12 rounded-2xl bg-[#25D366] group-hover:bg-[#1ebe5b] transition-colors flex items-center justify-center text-white">
                  <WhatsAppIcon className="w-6 h-6" />
                </div>
                <div>
                  <div className="text-[12px] uppercase tracking-wider text-brand-500">WhatsApp</div>
                  <div className="font-semibold text-brand-900">Direkt vom Handy schreiben</div>
                </div>
              </a>
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-2xl bg-brand-900 flex items-center justify-center text-white">
                  <MapPin className="w-5 h-5" />
                </div>
                <div>
                  <div className="text-[12px] uppercase tracking-wider text-brand-500">Adresse</div>
                  <div className="font-semibold text-brand-900">
                    {companyInfo.address.street}
                  </div>
                  <div className="text-brand-700">
                    {companyInfo.address.zip} {companyInfo.address.city}
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-10 rounded-2xl overflow-hidden border border-brand-100 aspect-[4/3]">
              <iframe
                title="Standort Kranenburg"
                src="https://www.openstreetmap.org/export/embed.html?bbox=6.0100%2C51.7770%2C6.0500%2C51.8000&layer=mapnik&marker=51.7880%2C6.0270"
                className="w-full h-full border-0"
                loading="lazy"
              />
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="bg-brand-50/60 rounded-3xl p-8 md:p-10 border border-brand-100">
              {sent ? (
                <div className="flex flex-col items-center text-center py-10" data-testid="contact-success">
                  <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center">
                    <CheckCircle2 className="w-8 h-8 text-emerald-700" />
                  </div>
                  <h3 className="mt-6 font-display text-2xl font-bold text-brand-900">
                    Vielen Dank!
                  </h3>
                  <p className="mt-3 text-brand-700 max-w-md">
                    Ihre Nachricht wurde erfolgreich übermittelt. Wir melden uns innerhalb von 24
                    Stunden bei Ihnen.
                  </p>
                  <Button
                    onClick={() => setSent(false)}
                    variant="outline"
                    className="mt-6 border-brand-300 text-brand-900"
                  >
                    Weitere Nachricht senden
                  </Button>
                </div>
              ) : (
                <form onSubmit={onSubmit} className="space-y-5" data-testid="contact-form">
                  <input
                    type="text"
                    name="website"
                    value={form.website}
                    onChange={onChange}
                    tabIndex={-1}
                    autoComplete="off"
                    className="hidden"
                    aria-hidden="true"
                  />
                  <div className="grid md:grid-cols-2 gap-5">
                    <div>
                      <label className="text-[13px] font-medium text-brand-800">Name *</label>
                      <Input
                        data-testid="contact-name-input"
                        name="name"
                        value={form.name}
                        onChange={onChange}
                        placeholder="Ihr Name"
                        className="mt-1.5 h-12 bg-white border-brand-200 focus-visible:ring-brand-500"
                      />
                    </div>
                    <div>
                      <label className="text-[13px] font-medium text-brand-800">E-Mail *</label>
                      <Input
                        data-testid="contact-email-input"
                        type="email"
                        name="email"
                        value={form.email}
                        onChange={onChange}
                        placeholder="ihre@email.de"
                        className="mt-1.5 h-12 bg-white border-brand-200 focus-visible:ring-brand-500"
                      />
                    </div>
                  </div>
                  <div className="grid md:grid-cols-2 gap-5">
                    <div>
                      <label className="text-[13px] font-medium text-brand-800">Telefon</label>
                      <Input
                        data-testid="contact-phone-input"
                        name="phone"
                        value={form.phone}
                        onChange={onChange}
                        placeholder="+49 ..."
                        className="mt-1.5 h-12 bg-white border-brand-200 focus-visible:ring-brand-500"
                      />
                    </div>
                  </div>
                  <div className="rounded-2xl border border-brand-100 bg-white/60 p-4" data-testid="property-block">
                    <div className="text-[12px] tracking-[0.2em] uppercase text-accent2 font-semibold mb-3">
                      Angaben zur Immobilie
                    </div>
                    <div className="grid md:grid-cols-2 gap-5">
                      <div>
                        <label className="text-[13px] font-medium text-brand-800">Ort der Immobilie *</label>
                        <Input
                          data-testid="contact-property-city-input"
                          name="property_city"
                          value={form.property_city}
                          onChange={onChange}
                          placeholder="z. B. Kleve"
                          className="mt-1.5 h-12 bg-white border-brand-200 focus-visible:ring-brand-500"
                        />
                      </div>
                      <div>
                        <label className="text-[13px] font-medium text-brand-800">Anzahl der Wohneinheiten *</label>
                        <Input
                          data-testid="contact-units-input"
                          name="units"
                          type="number"
                          min="1"
                          max="999"
                          inputMode="numeric"
                          value={form.units}
                          onChange={onChange}
                          placeholder="z. B. 8"
                          className="mt-1.5 h-12 bg-white border-brand-200 focus-visible:ring-brand-500"
                        />
                      </div>
                      <div>
                        <label className="text-[13px] font-medium text-brand-800">Aktuell verwaltet durch (optional)</label>
                        <Input
                          data-testid="contact-current-manager-input"
                          name="current_manager"
                          value={form.current_manager}
                          onChange={onChange}
                          placeholder="Name der bisherigen Verwaltung"
                          className="mt-1.5 h-12 bg-white border-brand-200 focus-visible:ring-brand-500"
                        />
                      </div>
                      <div>
                        <label className="text-[13px] font-medium text-brand-800">Gewünschter Starttermin</label>
                        <Input
                          data-testid="contact-desired-start-input"
                          name="desired_start"
                          value={form.desired_start}
                          onChange={onChange}
                          placeholder="z. B. 01.01.2027 oder schnellstmöglich"
                          className="mt-1.5 h-12 bg-white border-brand-200 focus-visible:ring-brand-500"
                        />
                      </div>
                    </div>
                  </div>
                  <div>
                    <label className="text-[13px] font-medium text-brand-800">Nachricht *</label>
                    <Textarea
                      data-testid="contact-message-input"
                      name="message"
                      value={form.message}
                      onChange={onChange}
                      placeholder="Erzählen Sie uns kurz von Ihrem Objekt ..."
                      rows={5}
                      className="mt-1.5 bg-white border-brand-200 focus-visible:ring-brand-500"
                    />
                  </div>
                  <CallbackPicker
                    value={form}
                    onChange={(patch) => setForm((s) => ({ ...s, ...patch }))}
                  />
                  <div className="flex items-start gap-3">
                    <Checkbox
                      data-testid="contact-consent-checkbox"
                      id="consent"
                      checked={form.consent}
                      onCheckedChange={(v) =>
                        setForm((s) => ({ ...s, consent: Boolean(v) }))
                      }
                      className="mt-1 data-[state=checked]:bg-brand-900 data-[state=checked]:border-brand-900"
                    />
                    <label
                      htmlFor="consent"
                      className="text-[13px] text-brand-700 leading-relaxed"
                    >
                      Ich stimme der Verarbeitung meiner personenbezogenen Daten gemäß der{" "}
                      <Link to="/datenschutz" className="underline hover:text-brand-900" data-testid="contact-privacy-link">
                        Datenschutzerklärung
                      </Link>{" "}
                      zu.
                    </label>
                  </div>
                  <Button
                    type="submit"
                    disabled={sending}
                    data-testid="contact-submit-button"
                    className="w-full md:w-auto bg-brand-900 hover:bg-brand-800 text-white rounded-full px-8 h-14"
                  >
                    {sending ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Send className="w-4 h-4 mr-2" />
                    )}
                    {sending ? "Wird gesendet …" : "Unverbindliches Angebot anfragen"}
                  </Button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
